/**
 * "Fill from tags": put the photos tagged "pool time" onto the spreads tagged "pool time".
 *
 * Explicit, never automatic (docs/00-decisions.md): the person tags, then clicks. What it does, in
 * order, for the spreads that carry a tag:
 *
 *   1. empty photo frames on a tagged spread take the matching photos first, oldest first, so a
 *      layout the person already chose is honoured and nothing they placed moves;
 *   2. what is left over goes onto new spreads inserted right after the LAST spread carrying that
 *      tag — or onto that spread itself when it still has no photo frames — one template per group,
 *      picked the same way the chronological auto-fill picks them;
 *   3. a photo carrying two tags goes to whichever tagged spread comes first and is never placed
 *      twice; photos already in the album, still processing, blurry or flagged as duplicates are
 *      skipped — a tag is for placing, and a soft or repeated picture is not what anyone wants
 *      on the page.
 *
 * Pure: same input, same commands. The caller applies them as one undo step. Commands are built
 * against a working copy so that inserting a spread keeps every later index right.
 */
import {
  PAGE_COUNT_LIMITS,
  applyCommand,
  emptySpread,
  familyOf,
  hasTag,
  innerPageCount,
  neutralImageFit,
  referencedPhotoIds,
  sharesTag,
  sortElements,
  tagKey,
  type AlbumDocument,
  type Command,
  type PhotoInfo,
} from '@freealbum/document';
import { DEFAULT_GAP_MS, dominantOrientation, groupPhotos, pickLayout, sortPhotosChronologically } from './autofill';
import { MAX_LAYOUT_PHOTOS } from './layouts/index';

export interface TagFillOptions {
  doc: AlbumDocument;
  photos: readonly PhotoInfo[];
  /** Only this spread's tags (overflow still lands right after it). Default: every tagged spread. */
  spreadIndex?: number;
  /** gap that starts a new group when laying out overflow, in ms (default 2 hours) */
  gapMs?: number;
  maxPerSpread?: number;
  /** insert spreads for what does not fit (default true; bounded by PAGE_COUNT_LIMITS) */
  growAlbum?: boolean;
}

export interface TagFillResult {
  commands: Command[];
  /** photos that received a frame */
  placed: number;
  /** matching photos left in the tray because they are blurry or a duplicate */
  skipped: number;
  /** matching photos left in the tray because the album is at its page limit or no template fit */
  unplaced: number;
  addedSpreads: number;
  /** indexes (in the resulting document) of the spreads that received photos */
  touched: number[];
}

const EMPTY: TagFillResult = { commands: [], placed: 0, skipped: 0, unplaced: 0, addedSpreads: 0, touched: [] };

const isFlagged = (p: PhotoInfo): boolean => p.flags.blurry || Boolean(p.flags.duplicateOf);

/** The photos the fill would consider for one spread: ready, unplaced, sharing a tag, not flagged. */
export function tagCandidates(doc: AlbumDocument, photos: readonly PhotoInfo[], spreadIndex: number): PhotoInfo[] {
  const spread = doc.spreads[spreadIndex];
  if (!spread || spread.tags.length === 0) return [];
  const used = referencedPhotoIds(doc);
  return photos.filter((p) => p.status === 'ready' && !used.has(p.id) && !isFlagged(p) && sharesTag(p.tags, spread.tags));
}

export function fillTaggedSpreads(opts: TagFillOptions): TagFillResult {
  let doc = opts.doc;
  const targets = doc.spreads
    .map((spread, index) => ({ spread, index }))
    .filter(({ spread, index }) => spread.tags.length > 0 && (opts.spreadIndex === undefined || index === opts.spreadIndex))
    .map(({ index }) => index);
  if (targets.length === 0) return EMPTY;

  const wanted = targets.flatMap((i) => doc.spreads[i]!.tags);
  const used = referencedPhotoIds(doc);
  const matching = opts.photos.filter((p) => p.status === 'ready' && !used.has(p.id) && sharesTag(p.tags, wanted));
  const skipped = matching.filter(isFlagged).length;
  // one pool, oldest first; a photo leaves it the moment something claims it
  let pool = sortPhotosChronologically(matching.filter((p) => !isFlagged(p)));
  if (pool.length === 0) return { ...EMPTY, skipped };

  const commands: Command[] = [];
  const touched = new Set<string>();
  const push = (cmd: Command) => {
    doc = applyCommand(doc, cmd);
    commands.push(cmd);
  };
  const take = (predicate: (p: PhotoInfo) => boolean): PhotoInfo | undefined => {
    const i = pool.findIndex(predicate);
    if (i < 0) return undefined;
    const [photo] = pool.splice(i, 1);
    return photo;
  };
  let placed = 0;
  let unplaced = 0;
  let addedSpreads = 0;

  // 1. empty frames on the tagged spreads, in album order
  for (const index of targets) {
    const spread = doc.spreads[index]!;
    for (const el of sortElements(spread.elements)) {
      if (el.type !== 'photo' || el.photoId !== null || el.locked || el.hidden) continue;
      const photo = take((p) => sharesTag(p.tags, spread.tags));
      if (!photo) break;
      push({
        type: 'element.update',
        target: index,
        id: el.id,
        patch: { photoId: photo.id, fit: neutralImageFit() },
      });
      touched.add(spread.id);
      placed++;
    }
  }

  // 2. overflow, one tag at a time, in the order the tags appear on the spreads
  const gapMs = opts.gapMs ?? DEFAULT_GAP_MS;
  const maxPerSpread = Math.min(opts.maxPerSpread ?? MAX_LAYOUT_PHOTOS, MAX_LAYOUT_PHOTOS);
  const family = familyOf(doc.page.widthMm, doc.page.heightMm);
  const order: string[] = [];
  for (const tag of wanted) if (!hasTag(order, tag)) order.push(tag);

  for (const tag of order) {
    const rest = pool.filter((p) => hasTag(p.tags, tag));
    if (rest.length === 0) continue;
    pool = pool.filter((p) => !hasTag(p.tags, tag));

    // Tagged spreads with no photo frames yet take the groups first, in album order; after that,
    // new spreads go in right after the last spread carrying the tag. Every empty target sits at
    // or before that anchor, so inserting after it never shifts one of them.
    let anchor = -1;
    const emptyTargets: number[] = [];
    doc.spreads.forEach((s, i) => {
      if (!hasTag(s.tags, tag)) return;
      anchor = i;
      if (!s.elements.some((el) => el.type === 'photo')) emptyTargets.push(i);
    });
    if (anchor < 0) {
      unplaced += rest.length;
      continue;
    }

    const groups = groupPhotos(rest, gapMs, maxPerSpread);
    groups.forEach((group, g) => {
      let index: number;
      const reuse = emptyTargets.shift();
      if (reuse !== undefined) {
        index = reuse;
      } else {
        if (!(opts.growAlbum ?? true) || innerPageCount(doc) + 2 > PAGE_COUNT_LIMITS.max) {
          unplaced += group.length;
          return;
        }
        index = anchor + 1;
        push({ type: 'spread.add', index, spread: { ...emptySpread(), tags: [tag] } });
        addedSpreads++;
      }
      const spread = doc.spreads[index]!;
      const layout = pickLayout({
        photoCount: group.length,
        orientation: dominantOrientation(group),
        family,
        scope: spread.single ? 'page' : 'spread',
        variant: g,
      });
      if (!layout) {
        unplaced += group.length;
        return;
      }
      push({ type: 'spread.applyLayout', index, layout, photoIds: group.map((p) => p.id), keepExisting: true });
      touched.add(spread.id);
      placed += group.length;
      anchor = Math.max(anchor, index);
    });
  }

  const touchedIndexes = doc.spreads.flatMap((s, i) => (touched.has(s.id) ? [i] : []));
  return { commands, placed, skipped, unplaced, addedSpreads, touched: touchedIndexes };
}

/** Which of `tags` a photo would be filed under first — for previews that group the tray by tag. */
export function firstSharedTag(photoTags: readonly string[], tags: readonly string[]): string | undefined {
  const keys = new Set(photoTags.map(tagKey));
  return tags.find((t) => keys.has(tagKey(t)));
}
